import { config } from '../config';
import { createPortalClient, type PortalClient } from './client';

/**
 * The one client the application uses.
 *
 * A module singleton rather than something only React can reach, because route
 * loaders are the portal's data layer and a loader runs outside the component
 * tree — it cannot call a hook. `ApiProvider` supplies this same instance to
 * components, so both paths share one CSRF refresher, one 401 latch and one
 * connectivity store. Two clients would mean two of each, and the second
 * would mint a cookie that invalidates the first's token.
 *
 * Built lazily so importing this module does not read the environment: `config()`
 * throws when `VITE_API_URL` is missing, and a throw at import time takes down
 * a test that never intended to make a request.
 */

let instance: PortalClient | undefined;

/** Late-bound so P0-WEB-07 can install the auth store's reaction without this
 *  module having to know the store exists. */
let unauthenticatedHandler: ((revoked: boolean) => void) | undefined;
let organisationIdSource: (() => string | undefined) | undefined;
let localeSource: (() => string) | undefined;

export function portalClient(): PortalClient {
  return (instance ??= createPortalClient({
    baseUrl: config().apiUrl,
    getOrganisationId: () => organisationIdSource?.(),
    getLocale: () => localeSource?.() ?? '',
    onUnauthenticated: (revoked) => unauthenticatedHandler?.(revoked),
  }));
}

/**
 * Installs the reaction to a 401. P0-WEB-07 calls this once with the auth
 * store's sign-out; the redirect itself stays in the loader that caught it.
 */
export function setUnauthenticatedHandler(handler: (revoked: boolean) => void): void {
  unauthenticatedHandler = handler;
}

/** Installs the selected organisation source (P0-PLAT-10 header). */
export function setOrganisationIdSource(source: () => string | undefined): void {
  organisationIdSource = source;
}

/**
 * The organisation the transport is currently acting for (P1-GEO-07).
 *
 * MapLibre builds its own tile requests and cannot go through the client, so the
 * tile URL has to carry the organisation itself. Reading it from the same source
 * the header uses — rather than from the session store directly — is what keeps
 * a tile scoped to whatever the last API call was scoped to. Two readers of the
 * same preference could disagree for exactly one render, which is the render
 * where the map shows the previous organisation's boundaries.
 */
export function currentOrganisationId(): string | undefined {
  return organisationIdSource?.();
}

/** Installs the current locale source for `Accept-Language`. */
export function setLocaleSource(source: () => string): void {
  localeSource = source;
}

/** Tests only. Module-level state leaks between tests otherwise. */
export function resetPortalClient(): void {
  instance = undefined;
  unauthenticatedHandler = undefined;
  organisationIdSource = undefined;
  localeSource = undefined;
}
